// Adopted from https://github.com/ueberdosis/tiptap/blob/main/demos/src/Experiments/Embeds/Vue/iframe.ts

import { Node, mergeAttributes } from '@tiptap/core'
import { getMarketUrlFromManifoldEmbedUrl } from './manifold-url'

export interface IframeOptions {
  HTMLAttributes: {
    [key: string]: any
  }
}

/** Returns true if the URL uses a safe protocol (http or https). */
function isSafeUrl(url: string | null | undefined): boolean {
  if (!url) return false
  try {
    const parsed = new URL(url, 'https://placeholder.invalid')
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    iframe: {
      setIframe: (options: { src: string }) => ReturnType
    }
  }
}

export default Node.create<IframeOptions>({
  name: 'iframe',

  group: 'block',

  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {
        class: 'w-full h-80',
        height: 80 * 4,
        sandbox:
          'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox',
      },
    }
  },

  addAttributes() {
    return {
      src: {
        default: null,
      },
      title: {
        default: 'Embedded content',
      },
      frameBorder: {
        default: 0,
      },
    }
  },

  parseHTML() {
    return [{ tag: 'iframe' }]
  },

  renderHTML({ HTMLAttributes }) {
    const iframeAttributes = mergeAttributes(
      HTMLAttributes,
      // Add options second, so they override originals, which prevents a XSS attack.
      this.options.HTMLAttributes
    )

    const allowedAttributes = [
      'src',
      'frameborder',
      'class',
      'width',
      'height',
      'title',
      'sandbox',
      'style',
      'allowfullscreen',
      'allow',
    ]
    for (const key of Object.keys(iframeAttributes)) {
      if (!allowedAttributes.includes(key.toLowerCase())) {
        delete iframeAttributes[key]
      }
    }

    const { src } = HTMLAttributes

    // Block dangerous protocols (javascript:, data:, vbscript:, etc.)
    if (!isSafeUrl(src)) {
      return ['div', { class: 'iframe-blocked' }]
    }

    // This is a hack to prevent native from opening the iframe in an in-app browser
    // and mobile in another tab. In native, links with target='_blank' open in the in-app browser.
    const manifoldMarketUrl = getMarketUrlFromManifoldEmbedUrl(src)
    if (manifoldMarketUrl) {
      return [
        'div',
        {
          style: {
            position: 'relative',
          },
          ...this.options.HTMLAttributes,
        },
        [
          'a',
          {
            href: manifoldMarketUrl,
            target: '_self',
            style: {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 20, // This is equivalent to tailwind's z-20
              display: 'block',
            },
          },
        ],
        ['iframe', iframeAttributes],
      ]
    }

    return ['iframe', iframeAttributes]
  },

  addCommands() {
    return {
      setIframe:
        (options: { src: string }) =>
        ({ tr, dispatch }) => {
          const { selection } = tr
          const node = this.type.create(options)

          if (dispatch) {
            tr.replaceRangeWith(selection.from, selection.to, node)
          }

          return true
        },
    }
  },
})
